import {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ForesightLink } from '@/components/link/foresight-link';
import SvgHome from '@/components/icons-eataly/SvgHome';
import { cn } from '@/lib/utils';
import { findPreviousBreadcrumbTarget, isBreadcrumbTrailOverflowing } from './breadcrumb-overflow';

/** Default nav strip background when a route `loader` omits `breadcrumbBackgroundClass`. */
export const EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS = 'bg-sand-95' as const;

export type EatalyBreadcrumbItem = {
    /** Stable key for list rendering */
    id: string;
    label: string;
    /**
     * When omitted, the item is rendered as plain text. Intermediate segments may omit `href` (e.g. no category id);
     * only the **last** list item should represent the current page — that one receives `aria-current="page"`.
     */
    href?: string;
};

export type EatalyBreadcrumbsProps = {
    items: EatalyBreadcrumbItem[];
    /** When true, a home icon link is shown before the trail (Figma). */
    showHome?: boolean;
    /** Destination for the home icon (site/locale added by `Link` when `SiteProvider` is present). */
    homeTo?: string;
    /** When true, home icon is rendered on the first trail segment instead of as a separate control. */
    mergeHomeWithFirstItem?: boolean;
    /** Accessibility label for the home icon control */
    homeAriaLabel?: string;
    /** Shown between segments; default is `/` per Figma */
    separator?: ReactNode;
    'aria-label'?: string;
    className?: string;
    /**
     * Background on the full-width nav strip. Set from the route `loader` (e.g. PLP `bg-neutral-white`, PDP `bg-sand-95`).
     * @default bg-sand-95
     */
    backgroundClassName?: string;
};

import { trimBreadcrumbTrailOnAncestorClick } from './trim-breadcrumb-trail';

/** Single-line mobile trail; only the last segment ellipsizes (full label stays in the DOM). */
const BREADCRUMB_ANCESTOR_LI_CLASS = 'flex shrink-0 items-center gap-2';
const BREADCRUMB_LAST_LI_CLASS = 'flex min-w-0 flex-1 items-center gap-2 overflow-hidden';
const BREADCRUMB_ANCESTOR_SEGMENT_CLASS = 'link-breadcrumb shrink-0 whitespace-nowrap';
const BREADCRUMB_LAST_SEGMENT_CLASS =
    'link-breadcrumb min-w-0 truncate lg:overflow-visible lg:whitespace-normal lg:text-clip';
const BREADCRUMB_LAST_SEGMENT_WITH_ICON_CLASS = 'link-breadcrumb flex min-w-0 items-center gap-2 overflow-hidden';
const BREADCRUMB_LAST_SEGMENT_LABEL_CLASS = 'min-w-0 truncate lg:overflow-visible lg:whitespace-normal lg:text-clip';
const BREADCRUMB_ANCESTOR_SEGMENT_WITH_ICON_CLASS =
    'link-breadcrumb inline-flex shrink-0 items-center gap-2 whitespace-nowrap';
const BREADCRUMB_ELLIPSIS_CLASS = 'link-breadcrumb shrink-0 whitespace-nowrap';
const BREADCRUMB_COLLAPSED_LAST_SEGMENT_CLASS = 'link-breadcrumb min-w-0 truncate';

function buildMergedHomeAriaLabel(homeAriaLabel: string, segmentLabel: string): string {
    return `${homeAriaLabel} - ${segmentLabel}`;
}

/**
 * Breadcrumb Eataly component (presentational). It only renders a pre-computed `items` trail.
 *
 * **Data source:** Build the trail in a route `loader` (e.g. with `buildEatalyCategoryBreadcrumbItems` /
 * `buildEatalyProductBreadcrumbItems` and expose `breadcrumbItems`). The app shell uses `useEatalyBreadcrumbs`
 * (returns `items` + `staleItemsWhilePending`) and `EatalyBreadcrumbsResolver` in `DefaultLayout`, so a deferred
 * PDP `Promise` still reaches `Await` while the previous PLP trail can show in the Suspense fallback. Use this
 * component directly when you already have a synchronous `EatalyBreadcrumbItem[]`.
 *
 * Style: `.link-breadcrumb` for typography and hover states.
 */
export default function EatalyBreadcrumbs({
    items,
    showHome = true,
    homeTo = '/',
    mergeHomeWithFirstItem = false,
    homeAriaLabel = 'Home',
    separator = '/',
    'aria-label': ariaLabel = 'Breadcrumb',
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
    className,
}: EatalyBreadcrumbsProps): ReactElement {
    const [optimisticItems, setOptimisticItems] = useState<EatalyBreadcrumbItem[] | null>(null);
    const itemsTrailKey = useMemo(
        () => items.map((item) => `${item.id}:${item.label}:${item.href ?? ''}`).join('|'),
        [items]
    );

    useEffect(() => {
        setOptimisticItems(null);
    }, [itemsTrailKey]);

    const displayItems = optimisticItems ?? items;
    const displayTrailKey = displayItems.map((item) => `${item.id}:${item.label}:${item.href ?? ''}`).join('|');
    const showStandaloneHome = showHome && !mergeHomeWithFirstItem;
    const previousTarget = findPreviousBreadcrumbTarget(displayItems);
    const listRef = useRef<HTMLOListElement>(null);
    const measureRef = useRef<HTMLOListElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const isCollapsed = isOverflowing && previousTarget != null;

    const handleAncestorClick = useCallback(
        (index: number) => {
            const trimmed = trimBreadcrumbTrailOnAncestorClick(items, index);
            if (trimmed) {
                setOptimisticItems(trimmed);
            }
        },
        [items]
    );

    const handleCollapsedPreviousClick = useCallback(() => {
        if (!previousTarget) {
            return;
        }

        const sourceIndex = items.findIndex((item) => item.id === previousTarget.item.id);
        handleAncestorClick(sourceIndex >= 0 ? sourceIndex : previousTarget.index);
    }, [handleAncestorClick, items, previousTarget]);

    useLayoutEffect(() => {
        const list = listRef.current;
        const measure = measureRef.current;
        if (!list || !measure) {
            return;
        }

        const update = () => {
            setIsOverflowing(isBreadcrumbTrailOverflowing(measure.scrollWidth, list.clientWidth));
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(list);

        let cancelled = false;
        void document.fonts?.ready.then(() => {
            if (!cancelled) {
                update();
            }
        });

        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [displayTrailKey, showStandaloneHome, mergeHomeWithFirstItem]);

    return (
        <nav aria-label={ariaLabel} className={cn(backgroundClassName, className)}>
            <div className="grid-global py-3">
                <div className="relative col-span-full min-w-0 overflow-hidden lg:col-start-2">
                    <ol
                        ref={measureRef}
                        aria-hidden
                        data-breadcrumb-measure="true"
                        className="invisible absolute top-0 left-0 flex w-max flex-nowrap items-center gap-2">
                        {showStandaloneHome && (
                            <li className="flex shrink-0 items-center gap-2">
                                <span className="link-breadcrumb inline-flex">
                                    <SvgHome className="h-4 w-4" width={16} height={16} />
                                </span>
                                <span className="link-breadcrumb">{separator}</span>
                            </li>
                        )}
                        {displayItems.map((item, index) => {
                            const isLast = index === displayItems.length - 1;
                            const isMergedHomeSegment = showHome && mergeHomeWithFirstItem && index === 0;

                            return (
                                <li key={item.id ?? `measure-${index}`} className="flex shrink-0 items-center gap-2">
                                    {index === 1 && <span className="link-breadcrumb">{separator}</span>}
                                    <span className="link-breadcrumb inline-flex items-center gap-2 whitespace-nowrap">
                                        {isMergedHomeSegment && <SvgHome className="h-4 w-4" width={16} height={16} />}
                                        {item.label}
                                    </span>
                                    {index > 0 && !isLast && <span className="link-breadcrumb">{separator}</span>}
                                </li>
                            );
                        })}
                    </ol>
                    <ol
                        ref={listRef}
                        data-breadcrumb-list="true"
                        className={cn(
                            'flex min-h-0 items-center gap-2',
                            isCollapsed
                                ? 'flex-nowrap overflow-hidden'
                                : 'flex-wrap overflow-hidden lg:overflow-visible'
                        )}>
                        {(showStandaloneHome || (isCollapsed && showHome)) && (
                            <li className="flex min-w-0 items-center gap-2">
                                <ForesightLink
                                    to={homeTo}
                                    hitSlop={10}
                                    name={`breadcrumb:${homeTo}`}
                                    className="link-breadcrumb inline-flex shrink-0"
                                    aria-label={homeAriaLabel}>
                                    <SvgHome className="h-4 w-4" width={16} height={16} aria-hidden />
                                </ForesightLink>
                                {!isCollapsed && (
                                    <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                                        {separator}
                                    </span>
                                )}
                            </li>
                        )}
                        {isCollapsed && previousTarget ? (
                            <>
                                <li className={BREADCRUMB_ANCESTOR_LI_CLASS}>
                                    <ForesightLink
                                        to={previousTarget.item.href ?? '/'}
                                        hitSlop={10}
                                        name={`breadcrumb:${previousTarget.item.href}`}
                                        className={BREADCRUMB_ELLIPSIS_CLASS}
                                        aria-label={previousTarget.item.label}
                                        onClick={handleCollapsedPreviousClick}>
                                        ...
                                    </ForesightLink>
                                </li>
                                <li className={BREADCRUMB_LAST_LI_CLASS}>
                                    <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                                        {separator}
                                    </span>
                                    <span className={BREADCRUMB_COLLAPSED_LAST_SEGMENT_CLASS} aria-current="page">
                                        {displayItems.at(-1)?.label}
                                    </span>
                                </li>
                            </>
                        ) : null}
                        {!isCollapsed &&
                            displayItems.map((item, index) => {
                                const key = item.id ?? `eataly-crumb-${index}`;
                                const isLast = index === displayItems.length - 1;
                                const isCurrentPage = !item.href && isLast;
                                const isMergedHomeSegment = showHome && mergeHomeWithFirstItem && index === 0;
                                const mergedHomeAriaLabel = isMergedHomeSegment
                                    ? buildMergedHomeAriaLabel(homeAriaLabel, item.label)
                                    : undefined;

                                return (
                                    <li
                                        key={key}
                                        className={isLast ? BREADCRUMB_LAST_LI_CLASS : BREADCRUMB_ANCESTOR_LI_CLASS}>
                                        {index === 1 && (
                                            <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                                                {separator}
                                            </span>
                                        )}
                                        {item.href && !isLast ? (
                                            <ForesightLink
                                                to={item.href}
                                                hitSlop={10}
                                                name={`breadcrumb:${item.href}`}
                                                className={
                                                    isMergedHomeSegment
                                                        ? BREADCRUMB_ANCESTOR_SEGMENT_WITH_ICON_CLASS
                                                        : BREADCRUMB_ANCESTOR_SEGMENT_CLASS
                                                }
                                                aria-label={mergedHomeAriaLabel}
                                                onClick={() => handleAncestorClick(index)}>
                                                {isMergedHomeSegment ? (
                                                    <>
                                                        <SvgHome
                                                            className="h-4 w-4 shrink-0"
                                                            width={16}
                                                            height={16}
                                                            aria-hidden
                                                        />
                                                        <span>{item.label}</span>
                                                    </>
                                                ) : (
                                                    item.label
                                                )}
                                            </ForesightLink>
                                        ) : isMergedHomeSegment ? (
                                            <span
                                                className={BREADCRUMB_LAST_SEGMENT_WITH_ICON_CLASS}
                                                aria-current={isCurrentPage ? 'page' : undefined}>
                                                <SvgHome
                                                    className="h-4 w-4 shrink-0"
                                                    width={16}
                                                    height={16}
                                                    aria-hidden
                                                />
                                                <span className={BREADCRUMB_LAST_SEGMENT_LABEL_CLASS}>
                                                    {item.label}
                                                </span>
                                            </span>
                                        ) : (
                                            <span
                                                className={BREADCRUMB_LAST_SEGMENT_CLASS}
                                                aria-current={isCurrentPage ? 'page' : undefined}>
                                                {item.label}
                                            </span>
                                        )}
                                        {index > 0 && !isLast && (
                                            <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                                                {separator}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                    </ol>
                </div>
            </div>
        </nav>
    );
}
